import React from "react";
import { createPortal } from "react-dom";
import ReactMarkdown from "react-markdown";
import rehypeSlug from "rehype-slug";
import rehypeAutolinkHeadings from "rehype-autolink-headings";
import mdContentRaw from "../legal/aviso-legal.md?raw";

// --- Utilidades ---
function normalizeMd(src) {
  return src
    .replace(/\r\n/g, "\n")
    .replace(/([^\n])\n(?=(\d+\)|\d+\.)\s)/g, "$1\n\n")
    .replace(/([^\n])\n(?=(-|\*)\s)/g, "$1\n\n")
    .replace(/\n{3,}/g, "\n\n");
}
const mdContent = normalizeMd(mdContentRaw);

function childrenToText(children) {
  return React.Children.toArray(children)
    .map((c) =>
      typeof c === "string"
        ? c
        : c?.props?.children
        ? childrenToText(c.props.children)
        : ""
    )
    .join("");
}

// --- Modal en Portal ---
export default function LegalModal({
  open,
  onClose,
  version,
  variant = "comfy", // compact | comfy | wide
}) {
  const scrollRef = React.useRef(null);
  const [percent, setPercent] = React.useState(0);

  // Monta portal a body
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => setMounted(true), []);

  // Cerrar con ESC
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Bloquear scroll del body mientras está abierto
  React.useEffect(() => {
    if (!open) return;
    const { overflow, paddingRight } = document.body.style;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    return () => {
      document.body.style.overflow = overflow || "";
      document.body.style.paddingRight = paddingRight || "";
    };
  }, [open]);

  // Progreso lectura
  const onScroll = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const max = el.scrollHeight - el.clientHeight;
    setPercent(max > 0 ? Math.min(100, Math.round((el.scrollTop / max) * 100)) : 0);
  }, []);

  // Reset al abrir
  React.useEffect(() => {
    if (open) {
      setPercent(0);
      requestAnimationFrame(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
      });
    }
  }, [open]);

  if (!open || !mounted) return null;

  // Anchos por variante
  const sizeByVariant =
    variant === "compact"
      ? "max-w-[720px]"
      : variant === "wide"
      ? "max-w-[1200px]"
      : "max-w-[980px]";

  const modal = (
    <div className="fixed inset-0 z-[1000]">
      {/* Fondo */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Decor (suaves) */}
      <div className="pointer-events-none absolute inset-0 -z-10 opacity-60">
        <div className="absolute -top-24 -left-24 h-[28rem] w-[28rem] rounded-full blur-3xl bg-gradient-to-tr from-indigo-200 via-sky-200 to-white" />
        <div className="absolute -bottom-24 -right-24 h-[28rem] w-[28rem] rounded-full blur-3xl bg-gradient-to-tr from-fuchsia-200 via-rose-200 to-white" />
      </div>

      {/* Contenedor centrado SIEMPRE */}
      <div className="absolute inset-0 p-4 sm:p-6 flex items-center justify-center">
        <div
          className={[
            "w-full",
            sizeByVariant,
            "bg-white/95 backdrop-blur shadow-2xl ring-1 ring-slate-900/10",
            "rounded-2xl sm:rounded-3xl",
            "grid grid-rows-[auto_1fr_auto]",
            "max-h-[90vh] overflow-hidden",
          ].join(" ")}
          role="dialog"
          aria-modal="true"
          aria-label="Aviso legal y Privacidad"
        >
          {/* Header */}
          <div className="border-b bg-white/90 backdrop-blur">
            <div className="flex items-center justify-between gap-3 px-4 sm:px-6 pt-3 pb-2">
              <div className="flex items-center gap-2">
                <span className="inline-block h-2.5 w-2.5 rounded-full bg-indigo-500" />
                <h2 className="text-base sm:text-lg font-semibold text-slate-900">
                  Aviso legal y Privacidad{version ? ` · v${version}` : ""}
                </h2>
              </div>
              <button
                className="text-slate-500 hover:text-slate-900 rounded-lg px-2 py-1"
                onClick={onClose}
                aria-label="Cerrar"
              >
                ✕
              </button>
            </div>
            <div className="px-4 sm:px-6 pb-2">
              <div className="h-1.5 w-full rounded-full bg-slate-200">
                <div
                  className="h-1.5 rounded-full bg-indigo-500 transition-all"
                  style={{ width: `${percent}%` }}
                />
              </div>
              <div className="mt-1 text-[11px] text-slate-500">{percent}% leído</div>
            </div>
          </div>

          {/* Body SCROLLABLE siempre */}
          <div ref={scrollRef} onScroll={onScroll} className="overflow-y-auto">
            <div className="w-full mx-auto px-4 sm:px-6 py-5 sm:py-8">
              <article
                className={[
                  "legal-doc-modal",
                  "prose prose-slate md:prose-lg",
                  "prose-headings:font-semibold",
                  "prose-h2:mt-10 prose-h2:mb-3 prose-h3:mt-7 prose-h3:mb-2",
                  "prose-a:text-indigo-600 hover:prose-a:text-indigo-700",
                  "prose-ol:pl-7 prose-ul:pl-7",
                  "prose-li:my-2",
                  "leading-relaxed tracking-[0.005em]",
                  "max-w-[80ch] mx-auto",
                ].join(" ")}
              >
                <ReactMarkdown
                  rehypePlugins={[
                    rehypeSlug,
                    [rehypeAutolinkHeadings, { behavior: "wrap", properties: { className: "no-anchor-icon" } }],
                  ]}
                  components={{
                    h2: ({ children, ...props }) => {
                      const txt = childrenToText(children).replace(/^\s*\d+\.\s*/, "");
                      return (
                        <h2 className="group scroll-mt-24 text-slate-900" {...props}>
                          {txt}
                        </h2>
                      );
                    },
                    h3: ({ children, ...props }) => {
                      const txt = childrenToText(children).replace(/^\s*\d+\.\s*/, "");
                      return (
                        <h3
                          className="group scroll-mt-24 text-slate-700 tracking-wider uppercase text-[0.95rem] font-semibold"
                          {...props}
                        >
                          {txt}
                        </h3>
                      );
                    },
                    ol: (p) => <ol className="list-decimal pl-7 [text-indent:0]" {...p} />,
                    ul: (p) => <ul className="list-disc pl-7 [text-indent:0]" {...p} />,
                    li: (p) => <li className="mb-2 leading-relaxed" {...p} />,
                    p: (p) => <p className="my-3" {...p} />,
                    strong: (p) => <strong className="font-semibold text-slate-900" {...p} />,
                  }}
                >
                  {mdContent}
                </ReactMarkdown>
              </article>
            </div>
          </div>

          {/* Footer */}
          <div
            className="flex items-center justify-end gap-3 px-4 sm:px-6 py-3 border-t bg-white/90 backdrop-blur"
            style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
          >
            {/*<a
              href="/legal"
              className="px-3 py-1.5 rounded-lg bg-white border border-slate-300 text-sm font-medium hover:bg-slate-50"
            >
              Ver página
            </a>
            */}
            
            <button
              onClick={onClose}
              className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>

      {/* Estilos extra */}
      <style>{`
        .legal-doc-modal { counter-reset: section; }
        .legal-doc-modal h2 { position: relative; padding-top: .35rem; }
        .legal-doc-modal h2::before {
          counter-increment: section;
          content: counter(section);
          display: inline-flex; align-items: center; justify-content: center;
          width: 2rem; height: 2rem; margin-right: .6rem; border-radius: 9999px;
          font-weight: 700; font-size: .9rem; color: #3730A3; background: #EEF2FF;
          box-shadow: inset 0 0 0 1px rgba(99,102,241,.25);
        }
        .legal-doc-modal h2::after {
          content: ""; display: block; height: 1px; margin-top: .55rem;
          background: linear-gradient(90deg,rgba(0,0,0,0) 0%, rgba(226,232,240,1) 20%, rgba(226,232,240,1) 80%, rgba(0,0,0,0) 100%);
        }
        .no-anchor-icon a { text-decoration: none !important; }
        .no-anchor-icon a::after { display: none !important; content: none !important; }
      `}</style>
    </div>
  );

  return createPortal(modal, document.body);
}
